import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../providers/AuthProvider';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { listIngredients } from '../graphql/ingredients';
import type { Ingredient } from '../types/ingredient';

const QUERY_TIMEOUT_MS = 10_000;

interface IngredientResponse {
  userId: string;
  ingredientId: string;
  name: string;
  brand?: string;
  strain?: string;
  varietal?: string;
  thc?: number;
  thca?: number;
  cbd?: number;
  cbda?: number;
  cbg?: number;
  cbn?: number;
  createdAt: string;
  updatedAt: string;
}

function mapResponseToIngredient(item: IngredientResponse): Ingredient {
  return {
    userId: item.userId,
    ingredientId: item.ingredientId,
    name: item.name,
    brand: item.brand,
    strain: item.strain,
    varietal: item.varietal as Ingredient['varietal'],
    cannabinoids: {
      thc: item.thc ?? undefined,
      thca: item.thca ?? undefined,
      cbd: item.cbd ?? undefined,
      cbda: item.cbda ?? undefined,
      cbg: item.cbg ?? undefined,
      cbn: item.cbn ?? undefined,
    },
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

function formatCannabinoids(ingredient: Ingredient): string[] {
  const labels: string[] = [];
  const { cannabinoids } = ingredient;

  if (cannabinoids.thc != null) labels.push(`THC: ${cannabinoids.thc}%`);
  if (cannabinoids.thca != null) labels.push(`THCa: ${cannabinoids.thca}%`);
  if (cannabinoids.cbd != null) labels.push(`CBD: ${cannabinoids.cbd}%`);
  if (cannabinoids.cbda != null) labels.push(`CBDA: ${cannabinoids.cbda}%`);
  if (cannabinoids.cbg != null) labels.push(`CBG: ${cannabinoids.cbg}%`);
  if (cannabinoids.cbn != null) labels.push(`CBN: ${cannabinoids.cbn}%`);

  return labels;
}

export function IngredientsListPage() {
  const { user } = useAuth();
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchIngredients = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const client = generateClient();
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('TIMEOUT')), QUERY_TIMEOUT_MS)
      );

      const request = client.graphql({
        query: listIngredients,
        variables: { userId: user.userId },
      });

      const response = await Promise.race([request, timeout]) as { data: { listIngredients: IngredientResponse[] } };
      const fetched = response.data.listIngredients.map(mapResponseToIngredient);

      // Sort by updatedAt descending (most recently updated first)
      fetched.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

      setIngredients(fetched);
    } catch (err) {
      if (err instanceof Error && err.message === 'TIMEOUT') {
        setError('The request timed out. Please check your connection and try again.');
      } else {
        setError('Failed to load ingredients. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchIngredients();
  }, [fetchIngredients]);

  if (isLoading) {
    return (
      <div className="page ingredients-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page ingredients-page">
        <h2>My Ingredients</h2>
        <ErrorMessage message={error} onRetry={fetchIngredients} />
      </div>
    );
  }

  if (ingredients.length === 0) {
    return (
      <div className="page ingredients-page">
        <h2>My Ingredients</h2>
        <p className="page-description">
          Manage your cannabis strain and brand profiles with detailed cannabinoid data.
        </p>
        <div className="empty-state">
          <p>No ingredients yet. Create your first ingredient to get started.</p>
          <div className="empty-state-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/ingredients/new" className="cta-button">Create Ingredient</Link>
            <Link to="/ingredients/import" className="cta-button-secondary">Import from Product</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page ingredients-page">
      <h2>My Ingredients</h2>
      <p className="page-description">
        Manage your cannabis strain and brand profiles with detailed cannabinoid data.
      </p>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Link to="/ingredients/new" className="cta-button">
          Create Ingredient
        </Link>
        <Link to="/ingredients/import" className="cta-button-secondary">
          Import from Product
        </Link>
      </div>
      <div className="ingredient-list">
        {ingredients.map((ingredient) => {
          const cannabinoidLabels = formatCannabinoids(ingredient);
          return (
            <Link
              key={ingredient.ingredientId}
              to={`/ingredients/${ingredient.ingredientId}`}
              className="ingredient-card"
            >
              <h3 className="ingredient-card-title">{ingredient.name}</h3>
              <div className="ingredient-card-meta">
                {ingredient.varietal && (
                  <span className="ingredient-card-varietal">{ingredient.varietal}</span>
                )}
                {cannabinoidLabels.length > 0 && (
                  <span className="ingredient-card-cannabinoids">
                    {cannabinoidLabels.map((label) => (
                      <span key={label} className="cannabinoid-badge">
                        {label}
                      </span>
                    ))}
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
